import {
  baseApi,
  INVOICE_LIST_TAGS,
  invoiceMutationTags,
} from "@/services/api/baseApi";
import { startUploadWatch } from "@/features/invoices/uploadWatchSlice";
import {
  extractPagination,
  mapCreationLog,
  mapInvoiceDetail,
  mapInvoiceList,
  mapStarredInvoiceList,
} from "@/services/invoices/invoiceMappers";
import {
  buildCreationLogQuery,
  buildInvoiceQuery,
} from "@/services/invoices/invoiceQueryParams";

/** `providesTags` for a paged list: the list itself plus each row. */
function listTags(type) {
  return (result) => [
    { type, id: "LIST" },
    ...(result?.items ?? []).map((item) => ({ type, id: item.id })),
  ];
}

export const invoicesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /* ---------------------------------------------------------------- lists */

    getInvoices: builder.query({
      query: (args) => `/invoices/?${buildInvoiceQuery(args)}`,
      transformResponse: (response, _meta, arg) =>
        mapInvoiceList(response, arg?.currentUserGuid),
      providesTags: listTags("Invoices"),
    }),

    getMyInvoices: builder.query({
      query: (args) => `/invoices/my/?${buildInvoiceQuery(args)}`,
      transformResponse: (response, _meta, arg) =>
        mapInvoiceList(response, arg?.currentUserGuid),
      providesTags: listTags("MyInvoices"),
    }),

    getStarredInvoices: builder.query({
      query: (args) => `/users/starred-invoices/?${buildInvoiceQuery(args)}`,
      transformResponse: (response, _meta, arg) =>
        mapStarredInvoiceList(response, arg?.currentUserGuid),
      providesTags: listTags("StarredInvoices"),
    }),

    getInvoiceByGuid: builder.query({
      // Accepts a plain guid or `{ guid, currentUserGuid }`.
      query: (arg) => `/invoices/${typeof arg === "string" ? arg : arg.guid}`,
      transformResponse: (response, _meta, arg) =>
        mapInvoiceDetail(
          response,
          typeof arg === "string" ? undefined : arg?.currentUserGuid,
        ),
      providesTags: (result, error, arg) => [
        {
          type: "InvoiceDetail",
          id: typeof arg === "string" ? arg : arg?.guid,
        },
      ],
    }),

    /* ------------------------------------------------------------ reference */

    getProcessingStatuses: builder.query({
      query: () => "/invoices/processing-statuses",
      providesTags: [{ type: "ProcessingStatuses", id: "LIST" }],
    }),

    getReviewStatuses: builder.query({
      query: () => "/invoices/review-statuses",
      providesTags: [{ type: "ReviewStatuses", id: "LIST" }],
    }),

    getReviewTypes: builder.query({
      query: () => "/invoices/review-types",
      providesTags: [{ type: "ReviewTypes", id: "LIST" }],
    }),

    getInvoiceCategories: builder.query({
      query: () => "/invoices/categories",
      providesTags: [{ type: "InvoiceCategories", id: "LIST" }],
    }),

    /* ------------------------------------------------------------- insights */

    // One call answers for every card: the counts per processing status, category
    // and failure type. It takes the list's own filters (no paging or sorting), and
    // each breakdown ignores its own filter, so a card's count is the number of
    // rows its click would list.
    getInvoiceInsights: builder.query({
      query: (args) => `/insights/invoices?${buildInvoiceQuery(args)}`,
      providesTags: [{ type: "InvoiceInsights", id: "ALL" }],
    }),

    getMyInvoiceInsights: builder.query({
      query: (args) => `/insights/invoices/my?${buildInvoiceQuery(args)}`,
      providesTags: [{ type: "MyInvoiceInsights", id: "ALL" }],
    }),

    /* --------------------------------------------------------------- audits */

    getAudits: builder.query({
      query: ({
        invoiceGuid,
        userGuid = "",
        query = "",
        page = 1,
        pageSize = 50,
      }) => {
        const params = new URLSearchParams({
          invoiceGuid: invoiceGuid ?? "",
          userGuid,
          query,
          page: String(page),
          pageSize: String(pageSize),
        });
        return `/audits?${params.toString()}`;
      },
      transformResponse: (response) => ({
        items: response?.items ?? [],
        pagination: extractPagination(response),
      }),
      providesTags: (result, error, arg) => [
        { type: "Audits", id: arg?.invoiceGuid },
      ],
    }),

    /* ----------------------------------------------------------------- logs */

    getInvoiceCreationLogs: builder.query({
      query: (args) => `/invoices/creation-logs?${buildCreationLogQuery(args)}`,
      transformResponse: (response) => ({
        items: (response?.items ?? []).map(mapCreationLog),
        pagination: extractPagination(response),
      }),
      providesTags: [{ type: "InvoiceCreationLogs", id: "LIST" }],
    }),

    /* ------------------------------------------------------------ mutations */

    createInvoice: builder.mutation({
      query: (formData) => ({
        url: "/invoices",
        method: "POST",
        body: formData,
        formData: true,
      }),
      invalidatesTags: [
        ...INVOICE_LIST_TAGS,
        { type: "InvoiceCreationLogs", id: "LIST" },
      ],
      // The POST returns before the backend lists the invoice, so the immediate
      // refetch above misses it. Watch for the returned guid (see uploadWatchSlice):
      // the lists poll until that invoice appears extracted, then stop.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const guid = typeof data === "string" ? data : data?.guid;
          if (guid) dispatch(startUploadWatch(guid));
        } catch {
          // A failed upload has nothing to wait for.
        }
      },
    }),

    updateInvoice: builder.mutation({
      query: ({ guid, data }) => ({
        url: `/invoices/${guid}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, arg) => invoiceMutationTags(arg.guid),
    }),

    updateInvoiceClient: builder.mutation({
      query: ({ guid, clientName }) => ({
        url: `/invoices/${guid}/client`,
        method: "PUT",
        body: { clientName },
      }),
      invalidatesTags: (result, error, arg) => invoiceMutationTags(arg.guid),
    }),

    updateInvoiceStatus: builder.mutation({
      query: ({ guid, processingStatusId, reviewStatusId }) => ({
        url: `/invoices/${guid}/status`,
        method: "PUT",
        body: { guid, processingStatusId, reviewStatusId },
      }),
      invalidatesTags: (result, error, arg) => invoiceMutationTags(arg.guid),
    }),

    /** Resubmit is a status change to Under Review / Review Required. */
    resubmitInvoice: builder.mutation({
      query: ({ guid }) => ({
        url: `/invoices/${guid}/status`,
        method: "PUT",
        body: { processingStatusId: 2, reviewStatusId: 4 },
      }),
      invalidatesTags: (result, error, arg) => invoiceMutationTags(arg.guid),
    }),

    updateInvoiceCategory: builder.mutation({
      query: ({ guid, categoryGuid }) => ({
        url: `/invoices/${guid}/category`,
        method: "PUT",
        body: { categoryGuid },
      }),
      invalidatesTags: (result, error, arg) => invoiceMutationTags(arg.guid),
    }),

    assignInvoice: builder.mutation({
      query: ({ guid, userGuid }) => ({
        url: `/invoices/${guid}:assign-to`,
        method: "POST",
        body: { userGuid },
      }),
      invalidatesTags: (result, error, arg) => invoiceMutationTags(arg.guid),
    }),

    deleteInvoice: builder.mutation({
      query: (guid) => ({
        url: `/invoices/${guid}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => invoiceMutationTags(arg),
    }),

    approveInvoice: builder.mutation({
      query: ({ guid, comment = "" }) => ({
        url: `/invoices/${guid}:approve`,
        method: "POST",
        body: { comment },
      }),
      invalidatesTags: (result, error, arg) => invoiceMutationTags(arg.guid),
    }),

    rejectInvoice: builder.mutation({
      query: ({ guid, comment = "", reason = "" }) => ({
        url: `/invoices/${guid}:reject`,
        method: "POST",
        body: { guid, comment, reason },
      }),
      invalidatesTags: (result, error, arg) => invoiceMutationTags(arg.guid),
    }),

    completeManualReview: builder.mutation({
      query: (guid) => ({
        url: `/invoices/${guid}:complete-manual-review`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => invoiceMutationTags(arg),
    }),

    extractInvoiceData: builder.mutation({
      query: (guid) => ({
        url: `/invoices/${guid}:run-data-extraction`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => invoiceMutationTags(arg),
    }),
  }),
});

export const {
  useGetInvoicesQuery,
  useGetMyInvoicesQuery,
  useGetStarredInvoicesQuery,
  useGetInvoiceByGuidQuery,
  useGetProcessingStatusesQuery,
  useGetReviewStatusesQuery,
  useGetReviewTypesQuery,
  useGetInvoiceCategoriesQuery,
  useGetInvoiceInsightsQuery,
  useGetMyInvoiceInsightsQuery,
  useGetAuditsQuery,
  useGetInvoiceCreationLogsQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useUpdateInvoiceClientMutation,
  useUpdateInvoiceStatusMutation,
  useResubmitInvoiceMutation,
  useUpdateInvoiceCategoryMutation,
  useAssignInvoiceMutation,
  useDeleteInvoiceMutation,
  useApproveInvoiceMutation,
  useRejectInvoiceMutation,
  useCompleteManualReviewMutation,
  useExtractInvoiceDataMutation,
} = invoicesApi;
